/**
 * Photo slots for personalized products.
 *
 * The slot count tracks the quantity selector: one photo per frame, four per
 * MemoryBox. Slots are cloned from the <template> in glowframe-uploader.liquid
 * and relabelled whenever quantity changes, so a customer ordering three frames
 * is asked for three photos.
 *
 * The files are NOT sent anywhere by this script. Each input is named
 * properties[...] and submits with the product form, so Shopify stores the
 * images itself and puts CDN links on the line item.
 */
if (!customElements.get('glowframe-uploader')) {
  // Shopify rejects line-item file uploads above 20 MB.
  const MAX_FILE_BYTES = 20 * 1024 * 1024;
  // Beyond this the page becomes unusable and the upload wait unreasonable, so
  // large orders are handed off to a conversation instead.
  const MAX_SLOTS = 20;

  customElements.define(
    'glowframe-uploader',
    class GlowframeUploader extends HTMLElement {
      connectedCallback() {
        this.grid = this.querySelector('[data-grid]');
        this.slotTemplate = this.querySelector('[data-slot-template]');
        this.heading = this.querySelector('[data-heading]');
        this.notice = this.querySelector('[data-notice]');

        this.photosPerUnit = Number(this.dataset.photosPerUnit) || 1;
        this.unitNoun = this.dataset.unitNoun || 'Item';
        this.baseLabel = this.dataset.baseLabel || 'Photo';

        this.sync();

        const quantity = this.quantityInput;
        if (quantity) {
          ['change', 'input'].forEach((event) =>
            quantity.addEventListener(event, () => this.sync())
          );
        }
      }

      /**
       * Dawn renders the quantity field outside the form and links it with a
       * form= attribute, so go through form.elements rather than the DOM tree.
       */
      get quantityInput() {
        const form = this.closest('form');
        if (!form) return null;
        const field = form.elements.quantity;
        if (!field) return null;
        return field.length ? field[0] : field;
      }

      get quantity() {
        const value = Number(this.quantityInput?.value);
        return Number.isFinite(value) && value >= 1 ? Math.floor(value) : 1;
      }

      get slots() {
        return Array.from(this.grid.querySelectorAll('[data-slot]'));
      }

      get required() {
        return this.dataset.required === 'true';
      }

      /** True once every visible slot holds a valid file. */
      get complete() {
        const slots = this.slots;
        return slots.length > 0 && slots.every((slot) => slot.dataset.ready === 'true');
      }

      /** Grows or shrinks the slot list to match quantity, keeping chosen files. */
      sync() {
        const wanted = Math.min(this.quantity * this.photosPerUnit, MAX_SLOTS);

        while (this.slots.length < wanted) this.grid.appendChild(this.createSlot());
        while (this.slots.length > wanted) this.destroySlot(this.slots[this.slots.length - 1]);

        this.relabel();
        this.updateHeading(wanted);
        this.updateNotice(wanted);
      }

      createSlot() {
        const slot = this.slotTemplate.content.firstElementChild.cloneNode(true);
        const input = slot.querySelector('input[type="file"]');
        input.addEventListener('change', () => this.onSelect(slot));
        return slot;
      }

      destroySlot(slot) {
        this.revokePreview(slot);
        slot.remove();
      }

      /**
       * Names are what reach the order, so they have to say exactly which photo
       * belongs to which piece: "Photo 2", or "Box 2 - Pic 3".
       */
      labelFor(index) {
        const single = this.quantity === 1;

        if (this.photosPerUnit === 1) {
          return single ? this.baseLabel : `${this.baseLabel} ${index + 1}`;
        }

        const unit = Math.floor(index / this.photosPerUnit) + 1;
        const within = (index % this.photosPerUnit) + 1;
        return single
          ? `${this.baseLabel} ${within}`
          : `${this.unitNoun} ${unit} - ${this.baseLabel} ${within}`;
      }

      relabel() {
        this.slots.forEach((slot, index) => {
          const label = this.labelFor(index);
          slot.dataset.slot = label;
          slot.querySelector('[data-label]').textContent = label;
          slot.querySelector('input[type="file"]').name = `properties[${label}]`;
        });
      }

      updateHeading(wanted) {
        if (!this.heading) return;
        this.heading.textContent = wanted > 1 ? 'Upload your photos' : 'Upload your photo';
      }

      updateNotice(wanted) {
        if (!this.notice) return;
        const requested = this.quantity * this.photosPerUnit;
        const capped = requested > wanted;
        this.notice.hidden = !capped;
        if (capped) {
          this.notice.textContent =
            `You can upload ${wanted} photos here — we'll message you to collect the remaining ` +
            `${requested - wanted} after you order.`;
        }
      }

      onSelect(slot) {
        const input = slot.querySelector('input[type="file"]');
        const file = input.files?.[0];

        if (!file) {
          this.reset(slot, 'Choose a photo', null);
          return;
        }

        if (file.size > MAX_FILE_BYTES) {
          // Clearing the input matters: leaving an oversized file attached would
          // let the form submit and fail server-side with a far worse message.
          input.value = '';
          this.reset(slot, window.glowframeStrings.fileTooLarge, 'error');
          return;
        }

        this.revokePreview(slot);
        const previewUrl = URL.createObjectURL(file);
        slot.dataset.previewUrl = previewUrl;
        slot.dataset.ready = 'true';

        this.renderPreview(slot, previewUrl);
        this.setStatus(slot, window.glowframeStrings.photoReady, 'ready');
      }

      reset(slot, message, state) {
        this.revokePreview(slot);
        slot.dataset.ready = 'false';
        const preview = slot.querySelector('[data-preview]');
        preview.innerHTML = '';
        preview.hidden = true;
        this.setStatus(slot, message, state);
      }

      revokePreview(slot) {
        if (slot.dataset.previewUrl) {
          URL.revokeObjectURL(slot.dataset.previewUrl);
          delete slot.dataset.previewUrl;
        }
      }

      renderPreview(slot, src) {
        const preview = slot.querySelector('[data-preview]');
        preview.innerHTML = '';
        const image = document.createElement('img');
        image.src = src;
        image.alt = '';
        preview.appendChild(image);
        preview.hidden = false;
      }

      setStatus(slot, message, state) {
        slot.querySelector('[data-status]').textContent = message;
        slot.classList.toggle('is-error', state === 'error');
        slot.classList.toggle('is-uploaded', state === 'ready');
      }
    }
  );
}
